import { RowData } from '@/utils/fileParsers'
import { ValidationResult } from '@/utils/rosterValidation'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface RosterPreviewTableProps {
  parsedData: RowData[]
  validationResult: ValidationResult | null
  getRowStatus: (rowIndex: number) => {
    errors: Array<{ message: string }>
    warnings: Array<{ message: string }>
    isValid: boolean
  } | null
  validRowCount: number
}

export function RosterPreviewTable({
  parsedData,
  validationResult,
  getRowStatus,
  validRowCount,
}: RosterPreviewTableProps) {
  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between'>
        <h4 className='text-sm font-semibold text-[#0F172A]'>
          Preview ({parsedData.length} record
          {parsedData.length !== 1 ? 's' : ''})
        </h4>
        {validationResult && (
          <div className='flex items-center gap-4 text-xs text-gray-600'>
            <div className='flex items-center gap-1'>
              <CheckCircle2 className='h-3 w-3 text-green-500' />
              <span>{validRowCount} Valid</span>
            </div>
            {validationResult.errors.length > 0 && (
              <div className='flex items-center gap-1'>
                <XCircle className='h-3 w-3 text-red-500' />
                <span>{validationResult.errors.length} Error</span>
              </div>
            )}
            {validationResult.warnings.length > 0 && (
              <div className='flex items-center gap-1'>
                <AlertCircle className='h-3 w-3 text-yellow-500' />
                <span>{validationResult.warnings.length} Warning</span>
              </div>
            )}
          </div>
        )}
      </div>
      <div className='border border-gray-200 rounded-lg overflow-hidden'>
        <div className='max-h-[400px] overflow-y-auto overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead className='bg-gray-50 sticky top-0 z-10'>
              <tr>
                <th className='px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b'>
                  Status
                </th>
                <th className='px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b'>
                  Row
                </th>
                <th className='px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b'>
                  First Name
                </th>
                <th className='px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b'>
                  Last Name
                </th>
                <th className='px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b'>
                  Email
                </th>
                <th className='px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b'>
                  Team
                </th>
              </tr>
            </thead>
            <tbody>
              {parsedData.map((row, index) => {
                const status = getRowStatus(index)
                const rowNum = index + 1
                const rowErrors = status?.errors || []
                const rowWarnings = status?.warnings || []
                const isValid = status?.isValid ?? false

                const hasIssues =
                  rowErrors.length > 0 || rowWarnings.length > 0
                const tooltipContent = hasIssues ? (
                  <div className='space-y-1'>
                    {rowErrors.length > 0 && (
                      <div>
                        <div className='font-semibold text-red-200 mb-1'>
                          Errors:
                        </div>
                        {rowErrors.map((err, errIdx) => (
                          <div
                            key={errIdx}
                            className='text-xs text-red-100'
                          >
                            • {err.message}
                          </div>
                        ))}
                      </div>
                    )}
                    {rowWarnings.length > 0 && (
                      <div>
                        <div className='font-semibold text-yellow-200 mb-1'>
                          Warnings:
                        </div>
                        {rowWarnings.map((warn, warnIdx) => (
                          <div
                            key={warnIdx}
                            className='text-xs text-yellow-100'
                          >
                            • {warn.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  'Valid - Ready to send'
                )

                return (
                  <tr
                    key={index}
                    className={cn(
                      'border-b transition-colors',
                      isValid
                        ? 'bg-green-50/50 hover:bg-green-50'
                        : 'bg-red-50/50 hover:bg-red-50'
                    )}
                  >
                    <td className='px-3 py-2'>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className='inline-block'>
                              {isValid ? (
                                <CheckCircle2 className='h-4 w-4 text-green-500' />
                              ) : (
                                <XCircle className='h-4 w-4 text-red-500' />
                              )}
                            </div>
                          </TooltipTrigger>
                          {hasIssues && (
                            <TooltipContent
                              side='right'
                              className='max-w-xs bg-gray-900 text-white p-3'
                            >
                              {tooltipContent}
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                    <td className='px-3 py-2 font-mono text-xs text-gray-600'>
                      {rowNum}
                    </td>
                    <td className='px-3 py-2'>{row.firstname}</td>
                    <td className='px-3 py-2'>{row.lastname}</td>
                    <td className='px-3 py-2 font-mono text-xs'>
                      {row.email}
                    </td>
                    <td className='px-3 py-2'>{row.team}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

